import * as React from 'react'
import { View, Animated, Dimensions, ScrollView, PanResponder } from 'react-native'
import styled from '@theme/styled-components'

const HEIGHT = Dimensions.get('window').height
const TOP = 50
const CLOSE_OFFSET = 60
const BOTTOM_DRAWER = 90

const Component = ({
  onClose,
  render,
  header,
  hideTarget,
  sticky,
  backgroundColor,
  alreadyOpen,
  offset = 0,
}: DrawerComponentProps) => {
  const translate = React.useMemo(() => new Animated.ValueXY({ x: 0, y: HEIGHT }), [])

  const panResponder = React.useMemo(
    () =>
      PanResponder.create({
        onPanResponderGrant: () => {
          translate.setOffset({
            x: (translate.x as any)._value,
            y: (translate.y as any)._value,
          })
        },
        onStartShouldSetPanResponder: (evt, gestureState) => true,
        onStartShouldSetPanResponderCapture: (evt, gestureState) => {
          return Math.abs(gestureState.dy) !== 0
        },
        onMoveShouldSetPanResponder: (evt, gestureState) => true,
        onMoveShouldSetPanResponderCapture: (evt, gestureState) => true,
        onShouldBlockNativeResponder: (evt, gestureState) => true,
        onPanResponderMove: Animated.event([null, { dy: translate.y }], { useNativeDriver: false }),
        onPanResponderRelease: (evt, gestureState) => {
          translate.flattenOffset()

          if (gestureState.dy > CLOSE_OFFSET) {
            if (!!sticky) {
              openSticky()
            } else {
              close()
            }
          } else {
            open()
          }
        },
      }),
    []
  )

  const HEADER_HEIGHT = hideTarget ? 20 : 44
  const SURFACE = HEIGHT - TOP - HEADER_HEIGHT - offset
  const open = () => {
    Animated.timing(translate, { toValue: { x: 0, y: TOP }, useNativeDriver: false, duration: 300 }).start()
  }

  const close = () => {
    Animated.timing(translate, { toValue: { x: 0, y: HEIGHT }, useNativeDriver: false, duration: 300 }).start(onClose)
  }

  const openSticky = () => {
    Animated.timing(translate, {
      toValue: { x: 0, y: HEIGHT - BOTTOM_DRAWER - offset },
      useNativeDriver: false,
      duration: 300,
    }).start()
  }

  React.useEffect(() => {
    if (!!sticky || alreadyOpen) {
      openSticky()
    } else {
      open()
    }
  }, [])

  const trans = translate.y.interpolate({ inputRange: [TOP, HEIGHT], outputRange: [TOP, HEIGHT], extrapolate: 'clamp' })
  const opacity = trans.interpolate({ inputRange: [TOP, HEIGHT], outputRange: [0.8, 0.2] })

  return (
    <MainContainer pointerEvents="box-none">
      {!sticky && <Back style={{ opacity }} pointerEvents="box-none" />}

      <PopupContainer style={{ transform: [{ translateY: trans }] }}>
        <ContentContainer backgroundColor={backgroundColor}>
          <DragContainer {...panResponder.panHandlers}>
            {!hideTarget && <Target />}
            <ScrollView showsVerticalScrollIndicator={false} bounces={false}>
              {!!header && header()}
            </ScrollView>
          </DragContainer>

          <View style={{ height: SURFACE }}>{render()}</View>
        </ContentContainer>
      </PopupContainer>
    </MainContainer>
  )
}

export default Component

const MainContainer = styled(Animated.View)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: transparent;
`

const Back = styled(Animated.View)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #0005;
`

// COMPONENT

const PopupContainer = styled(Animated.View)`
  ${(props) => props.theme.constants.shadow};
  position: absolute;
  left: 0;
  right: 0;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  overflow: hidden;
`

const ContentContainer = styled(View)<{ backgroundColor?: string }>`
  background-color: ${(props) => props.backgroundColor || props.theme.colors.backgroundSecondary};
`

const DragContainer = styled(View)`
  padding-top: 20px;
  background-color: transparent;
`

const Target = styled(View)`
  background-color: ${(props) => props.theme.colors.primaryLight};
  height: 4px;
  width: 40px;
  align-self: center;
  border-radius: 2px;
  margin-bottom: 20px;
`
